import { useEffect, useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export type Product = {
  id: number;
  lineCode: string;
  lineName: string;
  partNumber: string;
  description: string;
  uom: string;
  packQty: number;
  listPrice: string;
  showPrice: string | null;
  imageUrl: string | null;
  isFeatured: boolean;
};

export type CatalogueLine = { id: number; code: string; name: string; productCount: number };

export type Sort = 'pertinence' | 'part' | 'description' | 'prix';

export type Filters = {
  q: string;
  lineCode: string | null;
  sort: Sort;
  /** N'afficher que les produits vedettes. */
  featuredOnly?: boolean;
};

const PAGE_SIZE = 40;

type Page = { products: Product[]; total: number; offset: number; hasMore: boolean };

/**
 * Catalogue paginé. On charge par pages plutôt que tout d'un coup : le fichier
 * fournisseur fait déjà 408 produits, et le réseau d'une salle de congrès ne
 * pardonne pas les grosses réponses.
 */
export function useCatalogue(filters: Filters, enabled = true) {
  const params = (offset: number) => {
    const p = new URLSearchParams({
      limit: String(PAGE_SIZE),
      offset: String(offset),
      sort: filters.sort,
    });
    if (filters.q.trim()) p.set('q', filters.q.trim());
    if (filters.lineCode) p.set('lineCode', filters.lineCode);
    if (filters.featuredOnly) p.set('featured', 'true');
    return p.toString();
  };

  const query = useInfiniteQuery<Page>({
    queryKey: ['catalogue', filters],
    queryFn: ({ pageParam }) => api<Page>(`/api/products?${params(pageParam as number)}`),
    initialPageParam: 0,
    getNextPageParam: (last) => (last.hasMore ? last.offset + last.products.length : undefined),
    enabled,
  });

  return {
    products: query.data?.pages.flatMap((p) => p.products) ?? [],
    total: query.data?.pages[0]?.total ?? 0,
    isPending: query.isPending,
    isError: query.isError,
    error: query.error as Error | undefined,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
  };
}

/**
 * Marque/démarque un produit comme VEDETTE (admin seulement). Bascule l'étoile
 * IMMÉDIATEMENT (optimiste) dans toutes les pages de catalogue déjà chargées, puis
 * resynchronise — ce qui, en vue « Vedettes », retire le produit qu'on vient de
 * démarquer.
 */
export function useToggleFeatured() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, featured }: { productId: number; featured: boolean }) =>
      api(`/api/admin/products/${productId}/featured`, {
        method: 'POST',
        body: JSON.stringify({ featured }),
      }),
    onMutate: ({ productId, featured }) => {
      qc.setQueriesData<{ pages: Page[]; pageParams: unknown[] }>(
        { queryKey: ['catalogue'] },
        (old) =>
          old && Array.isArray(old.pages)
            ? {
                ...old,
                pages: old.pages.map((pg) => ({
                  ...pg,
                  products: pg.products.map((p) =>
                    p.id === productId ? { ...p, isFeatured: featured } : p,
                  ),
                })),
              }
            : old,
      );
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: ['catalogue'] }),
  });
}

/** Ajout MANUEL d'un produit (admin). Rafraîchit le catalogue au succès. */
export function useCreateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      lineId: number;
      partNumber: string;
      description: string;
      uom?: string;
      packQty: number;
      listPrice: number;
      showPrice?: number | null;
      isFeatured?: boolean;
    }) => api<{ product: Product }>('/api/admin/products', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['catalogue'] }),
  });
}

export function useCatalogueLines() {
  return useQuery<{ lines: CatalogueLine[] }>({
    queryKey: ['catalogue', 'lines'],
    queryFn: () => api('/api/products/lines'),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Diffère la valeur de recherche : on ne veut pas une requête par frappe sur un
 * clavier tactile. 250 ms est assez court pour paraître instantané.
 */
export function useDebounced<T>(value: T, delay = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

/**
 * Préférence d'affichage grille / liste, mémorisée et partagée entre le catalogue
 * et la prise de commande. Défaut : LISTE (on voit plus de produits d'un coup ;
 * la grille est jugée trop limitée). Nouvelle clé « vue-produits » pour repartir
 * du défaut, sans traîner l'ancien choix « grille ».
 */
export function useVueProduits() {
  const [vue, setVue] = useState<'grid' | 'list'>(() => {
    try {
      return localStorage.getItem('supershow.vue-produits') === 'grid' ? 'grid' : 'list';
    } catch {
      return 'list';
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem('supershow.vue-produits', vue);
    } catch {
      /* stockage indisponible : la préférence ne survivra pas au rechargement */
    }
  }, [vue]);
  return [vue, setVue] as const;
}

/** Prix réellement facturé : le prix show s'il existe, sinon le prix de liste. */
export function effectivePrice(p: Product): string {
  return p.showPrice ?? p.listPrice;
}

/** Pourcentage de rabais arrondi, ou null s'il n'y a pas de rabais à montrer. */
export function discountPercent(p: Product): number | null {
  if (!p.showPrice) return null;
  const list = Number(p.listPrice);
  const show = Number(p.showPrice);
  if (!(list > 0) || show >= list) return null;
  return Math.round((1 - show / list) * 100);
}
